// R11P GEOLOGICAL P0.5-P9E — authenticated Investor GIS client.
//
// Talks to exactly three geo-api endpoints, all authorized server-side on
// every request (P9E-B1):
//   GET /gis/investor/me/layers      the caller's OWN entitlement (session-scoped;
//                                    no investor id is ever sent)
//   GET /gis/layers/:id/features     one layer, only when the entitlement row says
//                                    operations.view === true
//   GET /gis/layers/:id/export       one layer, only when operations.export === true
//
// It never calls the generic catalogue, /gis/query or any download route (the
// geo-api has no separate GIS download route), and never builds an export from
// features already on screen. Results are returned raw-status so the caller
// can keep UNAVAILABLE, DENIED and EMPTY apart (src/lib/investorGis.ts).

import { geoApi, getGeoToken } from '@/lib/geoApi';
import {
  canExport,
  mayRequestFeatures,
  type ApiLikeResult,
  type InvestorGisLayer,
} from '@/lib/investorGis';

const NOT_ENTITLED: ApiLikeResult = {
  status: 403,
  data: null,
  error: 'This layer is not part of your authorized Investor GIS catalogue.',
  code: 'NOT_IN_ENTITLEMENT',
};

export const investorGisService = {
  /** The session investor's entitlement. No identifier is accepted or sent. */
  async getEntitlement(): Promise<ApiLikeResult> {
    const res = await geoApi.get<unknown>('/gis/investor/me/layers');
    return { status: res.status, data: res.data, error: res.error, code: res.code ?? null };
  },

  /**
   * Features for ONE layer. The request is issued only for a layer present in
   * the server entitlement with view === true; any other id (route, query
   * string, stale state) is refused locally without a request. The geo-api
   * still authorizes the read itself.
   */
  async getLayerFeatures(
    entitled: InvestorGisLayer[],
    layerId: string,
  ): Promise<ApiLikeResult> {
    if (!mayRequestFeatures(entitled, layerId)) return NOT_ENTITLED;
    const res = await geoApi.get<unknown>(
      `/gis/layers/${encodeURIComponent(layerId)}/features`,
    );
    return { status: res.status, data: res.data, error: res.error, code: res.code ?? null };
  },

  /**
   * Server-side export of ONE layer, offered only when the entitlement row
   * says operations.export === true. The geo-api produces the file and
   * re-authorizes EXPORT; the Portal only saves what it returns. The URL is
   * built from the configured API base and the entitled layer id — never from
   * a server-supplied link.
   */
  async exportLayer(
    layer: InvestorGisLayer,
  ): Promise<{ ok: boolean; status: number }> {
    if (!canExport(layer)) return { ok: false, status: 403 };
    const token = getGeoToken();
    try {
      const res = await fetch(
        `${geoApi.baseUrl}/gis/layers/${encodeURIComponent(layer.id)}/export`,
        {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        },
      );
      if (!res.ok) return { ok: false, status: res.status };
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${layer.slug.replace(/[^a-z0-9._-]+/gi, '-')}.geojson`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      return { ok: true, status: res.status };
    } catch {
      return { ok: false, status: 0 };
    }
  },
};
